import saveAs from 'file-saver';

import { PayloadRequest } from 'types/requestTypes';

import { omdApi } from '../../api';
import {
  CancelSplitRequestWorkRequest,
  ConfirmRequestWorkOutCheckTimeRequest,
  CreateWorkRequest,
  RequestExcelReport,
  RequestMixerReport,
  RequestPriceParams,
  RequestPriceResponse,
  RequestShortInfo,
  RequestWorkRedirect,
  RequestWorkStatistic,
  SplitRequestWorkRequest,
} from '../types';

const requestWork = omdApi.injectEndpoints({
  endpoints: (builder) => ({
    getRequestWork: builder.query<CreateWorkRequest, PayloadRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/${data.id}`,
      }),
    }),
    getRequestWorkStatistic: builder.query<RequestWorkStatistic, PayloadRequest>({
      query: (data) => ({
        url: `/v1/Request/${data.id}/statistic`,
      }),
    }),
    getRequestWorkShortInfo: builder.query<RequestShortInfo, PayloadRequest>({
      query: (data) => ({
        url: `/v1/Request/shortInfo/${data.id}`,
      }),
    }),
    requestWorkRedirect: builder.mutation<void, RequestWorkRedirect>({
      query: (data) => ({
        url: '/v1/RequestWork/redirect',
        method: 'PUT',
        body: data,
      }),
    }),
    changeRequestWork: builder.mutation<void, CreateWorkRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/${data.id}`,
        method: 'PUT',
        body: data,
      }),
    }),
    createRequestWork: builder.mutation<void, CreateWorkRequest>({
      query: (data) => ({
        url: `/v1/Request/${data.id}/work`,
        method: 'POST',
        body: data,
      }),
    }),
    deleteRequestWork: builder.mutation<void, PayloadRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/${data.id}`,
        method: 'DELETE',
      }),
    }),
    splitRequestWork: builder.mutation<void, SplitRequestWorkRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/${data.requestWorkId}/split`,
        method: 'POST',
        body: data,
      }),
    }),
    cancelSplitRequestWork: builder.mutation<void, CancelSplitRequestWorkRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/${data.requestWorkId}/cancelSplit`,
        method: 'POST',
        body: data,
      }),
    }),
    requestExcelReport: builder.query<void, RequestExcelReport>({
      query: (params) => ({
        url: '/v1/RequestWork/excelReport',
        method: 'GET',
        params,
        responseHandler: async (response) => {
          saveAs(new Blob([await response.blob()], { type: 'application/octet-stream' }), 'report.xlsx');
        },
        cache: 'no-cache',
      }),
    }),
    getRequestWorkOutCheckTime: builder.query({
      query: () => ({
        url: '/v1/RequestWork/outCheckTime',
      }),
    }),
    requestWorkMixerReport: builder.query<void, RequestMixerReport>({
      query: (data) => ({
        url: `v1/RequestWork/mixerReport/${data.transportId}`,
        params: {
          ...data,
        },
        responseHandler: async (response) => {
          saveAs(new Blob([await response.blob()], { type: 'application/octet-stream' }), 'report.xlsx');
        },
      }),
    }),
    confirmRequestWorkOutCheckTime: builder.mutation<void, ConfirmRequestWorkOutCheckTimeRequest>({
      query: (data) => ({
        url: `/v1/RequestWork/OutCheckTime/${data.requestWorkId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    requestPumpPrice: builder.query<RequestPriceResponse, RequestPriceParams>({
      query: (params) => ({
        url: 'v1/Request/pump-price',
        params,
      }),
    }),
  }),
});

export const {
  useChangeRequestWorkMutation,
  useCreateRequestWorkMutation,
  useDeleteRequestWorkMutation,
  useGetRequestWorkQuery,
  useLazyGetRequestWorkQuery,
  useRequestWorkRedirectMutation,
  useGetRequestWorkShortInfoQuery,
  useSplitRequestWorkMutation,
  useCancelSplitRequestWorkMutation,
  useLazyRequestExcelReportQuery,
  useConfirmRequestWorkOutCheckTimeMutation,
  useGetRequestWorkStatisticQuery,
  useLazyGetRequestWorkStatisticQuery,
  useLazyRequestWorkMixerReportQuery,
  useLazyRequestPumpPriceQuery,
} = requestWork;
